import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { PageContentShell } from "../../components/layout/PageContentShell";
import { PageHeader } from "../../components/Page/PageHeader";
import { StatusChip, TypeChip } from "../../components/StatusChip";
import { TicketActivity } from "../../components/TicketActivity";
import { TicketAnalysisPanel } from "../../components/TicketAnalysisPanel";
import { TicketHtml } from "../../components/TicketHtml";
import { TicketStatusFlow } from "../../components/TicketStatusFlow";
import { UserAvatar } from "../../components/UserAvatar";
import { hasCodeDevelopment, TICKET_STATUSES, ticketService, type Ticket } from "../../services/ticketService";
import { isSuperAdmin } from "../../utils/authorization";
import { apiMessage, businessErrorCode } from "../../utils/apiError";
import { displayName, formatDateTime } from "../../utils/display";

export function TicketDetailPage({ roles }: { roles: string[] }) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const admin = isSuperAdmin(roles);

  async function reload() {
    if (!id) return;
    const data = await ticketService.get(id);
    setTicket(data);
  }

  useEffect(() => {
    void reload().catch(() => setError(t("admin.actionFailed")));
  }, [id]);

  async function run(action: () => Promise<Ticket>) {
    setBusy(true);
    setError("");
    try {
      setTicket(await action());
    } catch (e) {
      setError(apiMessage(e) || t("admin.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function addComment(e: FormEvent) {
    e.preventDefault();
    if (!id || !comment.trim()) return;
    await run(() =>
      admin ? ticketService.adminComment(id, comment) : ticketService.comment(id, comment)
    );
    setComment("");
  }

  if (!ticket) {
    return (
      <PageContentShell>
        {error ? <Alert severity="error">{error}</Alert> : <Typography>…</Typography>}
      </PageContentShell>
    );
  }

  const canRetry = admin && ["DEV_FAILED", "OPEN", "ANALYZING", "NEEDS_CLARIFICATION"].includes(ticket.status);
  const needsClarification =
    ticket.status === "NEEDS_CLARIFICATION" || Boolean(ticket.analysis?.clarificationNeeded);
  const canDelete = admin && !hasCodeDevelopment(ticket);

  const onStatus = (status: Ticket["status"]) => {
    if (!id || status === ticket.status) return;
    void run(() => ticketService.updateStatus(id, status));
  };

  const onDelete = async () => {
    if (!id) return;
    setBusy(true);
    setError("");
    try {
      await ticketService.delete(id);
      setDeleteOpen(false);
      navigate(admin ? "/admin/tickets" : "/tickets");
    } catch (e) {
      setDeleteOpen(false);
      setError(
        businessErrorCode(e) === "TICKET_HAS_DEVELOPMENT"
          ? t("admin.deleteBlocked")
          : apiMessage(e) || t("admin.actionFailed")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContentShell>
      <PageHeader
        title={`${ticket.number} · ${ticket.title}`}
        actions={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusChip status={ticket.status} />
            <TicketStatusFlow status={ticket.status} admin={admin} onSelect={onStatus} />
            {admin ? (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t("admin.setStatus")}</InputLabel>
                <Select
                  label={t("admin.setStatus")}
                  value={ticket.status}
                  disabled={busy}
                  onChange={(event) => onStatus(event.target.value as Ticket["status"])}
                >
                  {TICKET_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`status.${status}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {canDelete ? (
              <Button color="error" variant="outlined" disabled={busy} onClick={() => setDeleteOpen(true)}>
                {t("admin.delete")}
              </Button>
            ) : null}
          </Stack>
        }
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {needsClarification ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {ticket.analysis?.clarificationPrompt || t("tickets.clarifyBody")}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TypeChip type={ticket.type} />
        <UserAvatar username={ticket.requesterUsername} email={ticket.requesterEmail} size={28} />
        <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
          <PersonOutlineIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2">
            {displayName(ticket.requesterUsername, ticket.requesterEmail)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
          <ScheduleOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2">{formatDateTime(ticket.createdDate, i18n.language)}</Typography>
        </Stack>
      </Stack>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("tickets.description")}
        </Typography>
        <TicketHtml html={ticket.description} />
      </Paper>
      {ticket.analysis ? <TicketAnalysisPanel analysis={ticket.analysis} /> : null}
      {(ticket.branchName || ticket.prUrl || ticket.runStatus || ticket.status === "DEV_FAILED") && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2">{t("tickets.development")}</Typography>
          {ticket.status === "DEV_FAILED" ? (
            <Typography>{t("tickets.devFailed")}</Typography>
          ) : (
            <>
              <Typography>
                {t("tickets.branch")}: {ticket.branchName || "—"}
              </Typography>
              <Typography>
                {t("tickets.pr")}: {ticket.prUrl || "—"}
              </Typography>
              <Typography>
                {t("tickets.run")}: {ticket.runStatus || "—"}
              </Typography>
            </>
          )}
        </Paper>
      )}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {t("tickets.activity")}
      </Typography>
      <TicketActivity comments={ticket.comments || []} />
      <Box component="form" onSubmit={addComment} sx={{ display: "flex", gap: 1, mb: 3 }}>
        <TextField
          fullWidth
          label={needsClarification ? t("tickets.clarifyComment") : admin ? t("admin.comment") : t("tickets.comment")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Button type="submit" variant="contained" disabled={busy}>
          {t("tickets.comment")}
        </Button>
      </Box>
      {admin ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {ticket.status === "SUPPORT_QUEUE" ? (
            <Button variant="outlined" disabled={busy} onClick={() => void run(() => ticketService.convert(ticket.id))}>
              {t("admin.convert")}
            </Button>
          ) : null}
          {["ANALYZED", "CONFLICT_REVIEW"].includes(ticket.status) ? (
            <Button variant="contained" disabled={busy} onClick={() => void run(() => ticketService.approve(ticket.id))}>
              {t("admin.approve")}
            </Button>
          ) : null}
          {canRetry ? (
            <Button variant="contained" disabled={busy} onClick={() => void run(() => ticketService.retry(ticket.id))}>
              {t("admin.retry")}
            </Button>
          ) : null}
          {["AUTO_REJECTED", "REJECTED"].includes(ticket.status) ? (
            <Button disabled={busy} onClick={() => void run(() => ticketService.reopen(ticket.id))}>
              {t("admin.reopen")}
            </Button>
          ) : null}
          {ticket.status !== "MERGED" && ticket.status !== "LIVE" ? (
            <Box sx={{ display: "flex", gap: 1, flexGrow: 1 }}>
              <TextField
                size="small"
                fullWidth
                label={t("admin.reason")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                color="error"
                variant="outlined"
                disabled={busy || !reason.trim()}
                onClick={() => void run(() => ticketService.reject(ticket.id, reason))}
              >
                {t("admin.reject")}
              </Button>
            </Box>
          ) : null}
        </Stack>
      ) : null}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t("admin.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("admin.deleteBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>{t("admin.cancel")}</Button>
          <Button color="error" variant="contained" onClick={() => void onDelete()} disabled={busy}>
            {t("admin.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentShell>
  );
}
